import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GradientButton } from '@/components/common/gradient-button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useAIGenerator } from './context'
import { ASPECT_RATIOS } from './constants'
import { useTranslations } from 'next-intl'

export function EditMode() {
  const {
    desc,
    setDesc,
    selectedRatio,
    isTooltipOpen,
    setIsTooltipOpen,
    createDisable,
    handleTooltipClick,
    handleRatioSelect,
    aiGenerator,
  } = useAIGenerator()

  const t = useTranslations('modals.ai-generator-v2')

  const selectedRatioConfig = ASPECT_RATIOS.find(
    (ratio) => ratio.value === selectedRatio,
  )

  return (
    <div className="w-full">
      <div className="w-full rounded-2xl border border-gray-200/50 dark:border-gray-800/50 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <div className="flex flex-col">
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={t('placeholder')}
            rows={3}
            disabled={createDisable}
            className={cn(
              'w-full text-lg px-6 py-4 rounded-2xl bg-transparent focus:outline-none resize-none placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:text-gray-100',
              createDisable && 'opacity-50 cursor-not-allowed',
            )}
          />

          <div className="flex justify-between items-center px-4 pt-2">
            {!createDisable ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <TooltipProvider>
                  <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleTooltipClick}
                        className="flex items-center gap-1 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                        aria-label={`Select aspect ratio (currently ${selectedRatio})`}
                      >
                        {/* {selectedRatioConfig?.icon} */}
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="14"
                            height="14"
                            rx="3"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M6 10V12C6 13.1046 6.89543 14 8 14H10"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M10 6H12C13.1046 6 14 6.89543 14 8V10"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>

                        {selectedRatio}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="start"
                      className="p-2 max-w-xs bg-[#212121] border-white/20 shadow-xl"
                      onClick={(e) => e.stopPropagation()}
                      sideOffset={8}
                    >
                      <p className="text-sm font-semibold mb-1.5 text-white">
                        {t('aspect_ratio')}
                      </p>
                      <div className="grid grid-cols-7 gap-1.5">
                        {ASPECT_RATIOS.map((ratio) => (
                          <button
                            type="button"
                            key={ratio.value}
                            onClick={() => handleRatioSelect(ratio.value)}
                            className={cn(
                              'flex flex-col items-center gap-0.5 p-1.5 rounded-md transition-all duration-200 hover:bg-white/10 hover:scale-105',
                              selectedRatio === ratio.value &&
                                'bg-purple-600/30 ring-1 ring-purple-500/50 hover:bg-purple-600/30',
                            )}
                            aria-label={`Set aspect ratio to ${ratio.label}`}
                            style={{
                              width: '36px',
                              height: '36px',
                            }}
                          >
                            <div className="text-white/80 scale-75">
                              {ratio.icon}
                            </div>
                            <span className="text-[10px] font-medium text-white/70 leading-tight">
                              {ratio.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-brand_2 pb-2">
                <LoaderCircle className="w-4 h-4 animate-spin" />
                <span className="text-xs">{t('generating')}</span>
              </div>
            )}

            {!createDisable && (
              <GradientButton
                onClick={aiGenerator}
                disabled={!desc || createDisable}
                className="px-6 py-2 rounded-lg font-medium transition-colors mb-2"
              >
                {t('generate')}
              </GradientButton>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
